import { BarChartOutlined, LineChartOutlined, ReloadOutlined, SortAscendingOutlined, SortDescendingOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, DatePicker, Empty, Row, Segmented, Select, Space, Spin, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StatCard } from "../../../components/statistics/StatCard";
import axiosInstance from "../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { LOGGED_USER } from "../../../variables/constants";
import {
    buildBarData,
    buildDailySeriesForCompany,
    computeGlobalTotals,
    findTopCompagnie,
    getCompagnieKey,
    getCompagnieLabel,
    metricList,
    resolveMetric,
    toNumber,
} from "./productionJournaliereUtils";

const { Title, Text } = Typography;

const formatMetricValue = (value, metric) => {
    const safeValue = displayNumbers(value || 0) ?? 0;
    return metric?.currency ? `${safeValue} FCFA` : safeValue;
};

const ProductionJournaliereCompagnie = () => {
    const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf("month"));
    const [selectedDay, setSelectedDay] = useState(dayjs());
    const [selectedCategorie, setSelectedCategorie] = useState("");
    const [metricKey, setMetricKey] = useState("primeTotale");
    const [sortOrder, setSortOrder] = useState("desc");
    const [selectedCompanyKey, setSelectedCompanyKey] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);

    const metric = resolveMetric(metricKey);

    const fetchCategories = useCallback(() => {
        axiosInstance
            .get(`/api/categorie`)
            .then((res) => setCategories(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setCategories([]));
    }, []);

    const loadProduction = useCallback(() => {
        const monthForApi = (selectedMonth || dayjs()).startOf("month").format("YYYY-MM-DD");
        setLoading(true);
        setError(null);

        axiosInstance
            .get(`/api/production/production-journaliere/${monthForApi}`, {
                params: selectedCategorie ? { categorie: selectedCategorie } : {},
            })
            .then((res) => {
                const payload = Array.isArray(res?.data) ? res.data : [];
                setData(payload);

                const firstKey = payload[0] ? getCompagnieKey(payload[0].compagnie) : "";
                setSelectedCompanyKey((prev) => {
                    const stillExists = payload.some((item) => getCompagnieKey(item.compagnie) === prev);
                    return stillExists ? prev : firstKey;
                });
            })
            .catch((err) => {
                console.error("Erreur lors du chargement de la production journalière", err);
                const message = err?.response?.data?.message || "Impossible de récupérer les données.";
                setError(message);
                setData([]);
                setSelectedCompanyKey("");
            })
            .finally(() => setLoading(false));
    }, [selectedCategorie, selectedMonth]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        loadProduction();
    }, [loadProduction]);

    useEffect(() => {
        // si le jour sélectionné n'est pas dans le mois, on recentre sur le début du mois
        if (!selectedDay.isSame(selectedMonth, "month")) {
            setSelectedDay(selectedMonth.startOf("month"));
        }
    }, [selectedMonth, selectedDay]);

    const userCompagnie = useMemo(() => {
        const sessionCompagnie = (() => {
            try {
                return JSON.parse(sessionStorage.getItem("currentCompagnie"));
            } catch {
                return null;
            }
        })();
        return (
            LOGGED_USER?.agence?.organisme ||
            LOGGED_USER?.organisme ||
            sessionCompagnie ||
            null
        );
    }, []);

    const isPoolUser = useMemo(() => {
        const type = LOGGED_USER?.agence?.typeAgence || LOGGED_USER?.typeAgence || "";
        return (type || "").toLowerCase() === "pool";
    }, []);

    const filteredData = useMemo(() => {
        if (isPoolUser) return data;
        const filterKey = getCompagnieKey(userCompagnie);
        if (!filterKey) return data;
        return data.filter((row) => getCompagnieKey(row?.compagnie) === filterKey);
    }, [data, isPoolUser, userCompagnie]);

    const compagniesOptions = useMemo(() => {
        const base = data;
        const opts = base.map((row) => ({
            label: getCompagnieLabel(row?.compagnie),
            value: getCompagnieKey(row?.compagnie),
        }));
        const unique = [];
        const seen = new Set();
        opts.forEach((opt) => {
            if (!seen.has(opt.value)) {
                seen.add(opt.value);
                unique.push(opt);
            }
        });
        if (isPoolUser) {
            return [{ label: "Toutes les compagnies", value: "" }, ...unique];
        }
        const locked = getCompagnieKey(userCompagnie);
        return unique.filter((opt) => !locked || opt.value === locked);
    }, [data, isPoolUser, userCompagnie]);

    useEffect(() => {
        if (!isPoolUser) {
            setSelectedCompanyKey(getCompagnieKey(userCompagnie));
        }
    }, [isPoolUser, userCompagnie]);

    const totals = useMemo(() => computeGlobalTotals(filteredData), [filteredData]);
    const barData = useMemo(
        () => buildBarData(filteredData, metricKey, sortOrder),
        [filteredData, metricKey, sortOrder]
    );
    const topCompagnie = useMemo(() => findTopCompagnie(filteredData, metricKey), [filteredData, metricKey]);

    useEffect(() => {
        if (!barData.length) {
            setSelectedCompanyKey("");
            return;
        }
        if (!isPoolUser) {
            const locked = getCompagnieKey(userCompagnie);
            if (locked && selectedCompanyKey !== locked) {
                setSelectedCompanyKey(locked);
                return;
            }
        }
        const exists = barData.some((bar) => bar.key === selectedCompanyKey);
        if (selectedCompanyKey && !exists) {
            setSelectedCompanyKey(barData[0].key);
        }
    }, [barData, isPoolUser, selectedCompanyKey, userCompagnie]);

    const { companyLabel, series } = useMemo(
        () => buildDailySeriesForCompany(filteredData, selectedCompanyKey, metricKey, selectedMonth),
        [filteredData, selectedCompanyKey, metricKey, selectedMonth]
    );

    const dailyTotals = useMemo(() => {
        const targetDay = selectedDay ? selectedDay.format("YYYY-MM-DD") : "";
        const sourceRows = selectedCompanyKey
            ? filteredData.filter((row) => getCompagnieKey(row?.compagnie) === selectedCompanyKey)
            : filteredData;
        return sourceRows.reduce(
            (acc, row) => {
                const match = (row?.productions || []).find(
                    (p) => dayjs(p.date).format("YYYY-MM-DD") === targetDay
                );
                acc.totalContrats += match ? toNumber(match.nombreContrats) : 0;
                acc.totalPrimeTotale += match ? toNumber(match.primeTotale) : 0;
                return acc;
            },
            { totalContrats: 0, totalPrimeTotale: 0 }
        );
    }, [filteredData, selectedCompanyKey, selectedDay]);

    const renderFilters = () => (
        <Space wrap size="middle" style={{ width: "100%", justifyContent: "space-between" }}>
            <Space wrap size="small">
                <DatePicker
                    picker="month"
                    allowClear={false}
                    value={selectedMonth}
                    onChange={(value) => setSelectedMonth(value ? value.startOf("month") : dayjs().startOf("month"))}
                    style={{ minWidth: 180 }}
                    format="MMMM YYYY"
                />
                <DatePicker
                    allowClear={false}
                    value={selectedDay}
                    onChange={(value) => setSelectedDay(value || dayjs())}
                    style={{ minWidth: 160 }}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => current && !current.isSame(selectedMonth, "month")}
                />
                <Select
                    showSearch
                    value={selectedCategorie || ""}
                    onChange={(value) => setSelectedCategorie(value || "")}
                    style={{ minWidth: 200 }}
                    placeholder="Catégorie"
                    optionFilterProp="label"
                    options={[
                        { label: "Toutes les catégories", value: "" },
                        ...categories.map((cat) => ({
                            label: `${cat.codeCategorie} - ${cat.nom}`,
                            value: cat.codeCategorie,
                        })),
                    ]}
                />
                <Select
                    showSearch
                    value={selectedCompanyKey}
                    onChange={(value) => setSelectedCompanyKey(value)}
                    style={{ minWidth: 220 }}
                    placeholder="Compagnie"
                    optionFilterProp="label"
                    disabled={!isPoolUser}
                    options={compagniesOptions}
                />
                <Segmented
                    options={metricList.map((m) => ({ label: m.label, value: m.key }))}
                    value={metricKey}
                    onChange={(val) => setMetricKey(val)}
                />
            </Space>
            <Space>
                <Tooltip title={`Tri ${sortOrder === "asc" ? "croissant" : "décroissant"}`}>
                    <Button
                        icon={sortOrder === "asc" ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
                        onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
                    />
                </Tooltip>
                <Tooltip title="Actualiser">
                    <Button icon={<ReloadOutlined />} onClick={loadProduction} />
                </Tooltip>
            </Space>
        </Space>
    );

    const renderKpis = () => (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} lg={6}>
                <StatCard
                    title="Contrats (jour)"
                    value={formatMetricValue(dailyTotals.totalContrats, resolveMetric("contrats"))}
                    subtitle={selectedDay ? `Jour ${selectedDay.format("DD/MM/YYYY")}` : "Jour sélectionné"}
                    icon="bx bx-file"
                    iconBg="linear-gradient(135deg, #1890ff 0%, #242166 100%)"
                    gradient="linear-gradient(135deg, #1890ff 0%, #242166 100%)"
                />
            </Col>
            <Col xs={24} sm={12} lg={6}>
                <StatCard
                    title="Prime totale (jour)"
                    value={formatMetricValue(dailyTotals.totalPrimeTotale, resolveMetric("primeTotale"))}
                    subtitle={selectedDay ? `Jour ${selectedDay.format("DD/MM/YYYY")}` : "Jour sélectionné"}
                    icon="bx bx-coin"
                    iconBg="linear-gradient(135deg, #faad14 0%, #f759ab 100%)"
                    gradient="linear-gradient(135deg, #faad14 0%, #f759ab 100%)"
                />
            </Col>
            <Col xs={24} sm={12} lg={6}>
                <StatCard
                    title="Contrats (mois)"
                    value={formatMetricValue(totals.totalContrats, resolveMetric("contrats"))}
                    subtitle={`Total ${selectedMonth.format("MMMM YYYY")}`}
                    icon="bx bx-collection"
                    iconBg="linear-gradient(135deg, #36cfc9 0%, #13c2c2 100%)"
                    gradient="linear-gradient(135deg, #36cfc9 0%, #13c2c2 100%)"
                />
            </Col>
            <Col xs={24} sm={12} lg={6}>
                <StatCard
                    title="Prime totale (mois)"
                    value={formatMetricValue(totals.totalPrimeTotale, resolveMetric("primeTotale"))}
                    subtitle={`Total ${selectedMonth.format("MMMM YYYY")}`}
                    icon="bx bx-coin-stack"
                    iconBg="linear-gradient(135deg, #722ed1 0%, #2f54eb 100%)"
                    gradient="linear-gradient(135deg, #722ed1 0%, #2f54eb 100%)"
                />
            </Col>
        </Row>
    );

    const renderBarChart = () => {
        if (!barData.length) {
            return <Empty description="Aucune production sur ce mois" />;
        }

        const maxValue = Math.max(...barData.map((bar) => bar.value), 1);

        return (
            <div style={{ overflowX: "auto", paddingBottom: 8 }}>
                <div style={{ minHeight: 260, display: "flex", alignItems: "flex-end", gap: 18, padding: "6px 4px" }}>
                    {barData.map((bar) => {
                        const heightPct = Math.max((bar.value / maxValue) * 100, 8);
                        const isActive = selectedCompanyKey === bar.key;
                        return (
                            <div key={bar.key || bar.label} style={{ minWidth: 90, textAlign: "center" }}>
                                <Tooltip title={`${bar.label}: ${formatMetricValue(bar.value, metric)}`}>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedCompanyKey(bar.key)}
                                        onKeyDown={(e) => (e.key === "Enter" ? setSelectedCompanyKey(bar.key) : null)}
                                        style={{
                                            height: 200,
                                            display: "flex",
                                            alignItems: "flex-end",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 28,
                                                height: `${heightPct}%`,
                                                minHeight: 12,
                                                borderRadius: "12px 12px 6px 6px",
                                                background: isActive
                                                    ? "linear-gradient(180deg, #242166 0%, #5b8def 100%)"
                                                    : "linear-gradient(180deg, #4bc0c8 0%, #5b8def 100%)",
                                                boxShadow: isActive
                                                    ? "0 8px 16px rgba(36,33,102,0.25)"
                                                    : "0 4px 10px rgba(0,0,0,0.08)",
                                                transition: "all 0.3s ease",
                                            }}
                                        />
                                    </div>
                                </Tooltip>
                                <div style={{ marginTop: 10, fontWeight: isActive ? 700 : 500, fontSize: 12, color: "#262626" }}>
                                    {bar.label}
                                </div>
                                <div style={{ fontSize: 12, color: "#8c8c8c" }}>{formatMetricValue(bar.value, metric)}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderLineChart = () => {
        if (!series.length) {
            return <Empty description="Sélectionnez une compagnie" />;
        }

        const width = 640;
        const height = 260;
        const padding = 36;
        const innerWidth = width - padding * 2;
        const innerHeight = height - padding * 2;
        const maxValue = Math.max(...series.map((p) => p.value), 1);
        const points = series.map((point, index) => {
            const x =
                padding +
                (innerWidth * (series.length === 1 ? 0 : index)) /
                Math.max(series.length - 1, 1);
            const y = padding + innerHeight - (innerHeight * point.value) / maxValue;
            return { ...point, x, y };
        });
        const pathD = points.map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
        const xLabels = [points[0], points[Math.floor(points.length / 2)], points[points.length - 1]].filter(Boolean);

        return (
            <div style={{ width: "100%", height: "100%" }}>
                <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width: "100%", height: "220px" }}>
                    <defs>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#242166" stopOpacity="0.6" />
                            <stop offset="100%" stopColor="#5b8def" stopOpacity="0.05" />
                        </linearGradient>
                    </defs>
                    <rect x="0" y="0" width={width} height={height} fill="transparent" />
                    {[0.25, 0.5, 0.75].map((ratio) => {
                        const y = padding + innerHeight * ratio;
                        return <line key={ratio} x1={padding} x2={width - padding} y1={y} y2={y} stroke="#f0f0f0" strokeWidth={1} />;
                    })}
                    <path d={pathD} fill="none" stroke="#242166" strokeWidth={2} />
                    <path
                        d={`${pathD} L ${points[points.length - 1]?.x} ${height - padding} L ${points[0]?.x} ${height - padding} Z`}
                        fill="url(#lineGradient)"
                        opacity={0.2}
                    />
                    {points.map((p) => (
                        <g key={p.date}>
                            <circle cx={p.x} cy={p.y} r={4} fill="#242166" />
                            <title>
                                {`${p.date} : ${formatMetricValue(p.value, metric)}`}
                            </title>
                        </g>
                    ))}
                    {xLabels.map((label) => (
                        <text key={label.date} x={label.x} y={height - padding + 18} textAnchor="middle" fontSize="10" fill="#8c8c8c">
                            {dayjs(label.date).format("DD/MM")}
                        </text>
                    ))}
                </svg>
            </div>
        );
    };

    return (
        <Card
            title={
                <Space>
                    <BarChartOutlined />
                    <span>Production journalière par compagnie</span>
                </Space>
            }
            extra={<Text type="secondary">Mois de {selectedMonth.format("MMMM YYYY")}</Text>}
            style={{ marginTop: 24 }}
            bodyStyle={{ padding: 20 }}
        >
            <Spin spinning={loading} tip="Chargement...">
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    {renderFilters()}
                    {error && <Alert type="error" message="Erreur de chargement" description={error} showIcon />}
                    {renderKpis()}
                    <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                        <Col xs={24} xl={14}>
                            <Card
                                bordered={false}
                                bodyStyle={{ padding: 16 }}
                                title={
                                    <Space>
                                        <BarChartOutlined />
                                        <span>Comparaison par compagnie ({metric.label})</span>
                                    </Space>
                                }
                            >
                                {renderBarChart()}
                            </Card>
                        </Col>
                        <Col xs={24} xl={10}>
                            <Card
                                bordered={false}
                                bodyStyle={{ padding: 16 }}
                                title={
                                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                                        <Space>
                                            <LineChartOutlined />
                                            <span>Evolution journalière</span>
                                        </Space>
                                        <Select
                                            showSearch
                                            size="small"
                                            value={selectedCompanyKey || undefined}
                                            style={{ minWidth: 180 }}
                                            placeholder="Compagnie"
                                            onChange={(value) => setSelectedCompanyKey(value)}
                                            options={barData.map((bar) => ({ label: bar.label, value: bar.key }))}
                                        />
                                    </Space>
                                }
                                extra={<Text type="secondary">{companyLabel}</Text>}
                            >
                                {renderLineChart()}
                                <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                                    Cliquez sur une barre pour voir le détail journalier.
                                </Text>
                            </Card>
                        </Col>
                    </Row>
                </Space>
            </Spin>
        </Card>
    );
};

export default ProductionJournaliereCompagnie;
